"use client";

import { useCallback, useRef } from "react";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { useWaveAbortController } from "./useWaveAbortController";
import { WaveDataStoreUpdater } from "./types";
import { fetchLightWaveMessages, fetchWaveMessages, fetchAroundSerialNoWaveMessages } from "../utils/wave-messages-utils";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { ApiLightDrop } from "@/generated/models/ApiLightDrop";
import { WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";

// Tracks which waves are currently loading next page
interface PaginationState {
  isLoading: boolean;
  promise: Promise<(ApiDrop | ApiLightDrop)[] | null> | null;
}
export type NextPageProps = NextPageFullProps | NextPageLightProps;
interface NextPageFullProps {
  readonly waveId: string;
  readonly type: DropSize.FULL;
}
interface NextPageLightProps {
  readonly waveId: string;
  readonly type: DropSize.LIGHT;
  readonly targetSerialNo: number;
}

// Tracks the state for fetching messages around a specific serial number
interface AroundSerialNoState {
  isFetching: boolean;
  pendingSerialNo: number | null;
}
export function useWavePagination({
  updateData,
  getData
}: WaveDataStoreUpdater) {
  // Use the common hooks
  const {
    cancelFetch: cancelAbort,
    createController,
    cleanupController
  } = useWaveAbortController();

  // Track pagination loading state
  const paginationStates = useRef<Record<string, PaginationState>>({});
  // Track state for fetching around a serial number
  const aroundSerialNoStates = useRef<Record<string, AroundSerialNoState>>({});
  const aroundQueueLastSuccessfullyFetchedSerialNoRef = useRef<number | null>(null);
  const aroundQueueLastFetchedMinSerialNoRef = useRef<number | null>(null);
  const aroundQueueLastFetchedMaxSerialNoRef = useRef<number | null>(null);
  const determineSerialToFetch = useCallback((pendingSerialNo: number | null): number | null => {
    if (pendingSerialNo === null) {
      return null;
    }
    const lastSuccessfullyFetchedSerialNo = aroundQueueLastSuccessfullyFetchedSerialNoRef.current;
    if (lastSuccessfullyFetchedSerialNo === null) {
      return pendingSerialNo;
    }
    if (pendingSerialNo === lastSuccessfullyFetchedSerialNo) {
      return pendingSerialNo;
    }
    if (pendingSerialNo > lastSuccessfullyFetchedSerialNo) {
      const lastFetchedMaxSerialNo = aroundQueueLastFetchedMaxSerialNoRef.current;
      if (lastFetchedMaxSerialNo === null) {
        return pendingSerialNo;
      }
      if (pendingSerialNo > lastFetchedMaxSerialNo + WAVE_DROPS_PARAMS.limit) {
        return pendingSerialNo;
      }
      return lastFetchedMaxSerialNo + (WAVE_DROPS_PARAMS.limit - 1);
    } else {
      // pendingSerialNo < lastSuccessfullyFetchedSerialNo
      const lastFetchedMinSerialNo = aroundQueueLastFetchedMinSerialNoRef.current;
      if (lastFetchedMinSerialNo === null) {
        return pendingSerialNo;
      }
      if (pendingSerialNo < lastFetchedMinSerialNo - WAVE_DROPS_PARAMS.limit) {
        return pendingSerialNo;
      }
      return lastFetchedMinSerialNo - (WAVE_DROPS_PARAMS.limit - 1);
    }
  }, [] // No dependencies as it only uses refs
  );

  /**
   * Gets the oldest message's serial number for pagination
   */
  const getOldestMessageSerialNo = useCallback((waveId: string): number | null => {
    const data = getData(waveId);
    if (!data?.drops.length) {
      return null;
    }

    // Sort by serial_no and get the smallest (oldest) one
    const serialNos = data.drops.map(drop => drop.serial_no);
    return Math.min(...serialNos);
  }, [getData]);

  /**
   * Updates store with new paginated data
   */
  const updateWithPaginatedData = useCallback((waveId_0: string, newDrops: (ApiDrop | ApiLightDrop)[] | null) => {
    // Clear the loading state
    if (paginationStates.current[waveId_0]) {
      paginationStates.current[waveId_0].isLoading = false;
      paginationStates.current[waveId_0].promise = null;
    }
    if (newDrops === null) {
      return null;
    }
    if (!newDrops?.length) {
      // No more drops to load, update hasNextPage
      const currentData = getData(waveId_0);
      if (currentData) {
        updateData({
          key: waveId_0,
          isLoadingNextPage: false,
          hasNextPage: false
        });
      }
      return null;
    }
    const currentData_0 = getData(waveId_0);
    if (!currentData_0) {
      return null;
    }
    updateData({
      key: waveId_0,
      isLoadingNextPage: false,
      hasNextPage: newDrops.length > 0,
      drops: newDrops.map(drop_0 => {
        if ("part_1_text" in drop_0) {
          return {
            ...drop_0,
            waveId: waveId_0,
            type: DropSize.LIGHT,
            stableKey: drop_0.id,
            stableHash: drop_0.id
          };
        }
        return {
          ...drop_0,
          type: DropSize.FULL,
          stableKey: drop_0.id,
          stableHash: drop_0.id
        };
      })
    });
    return newDrops;
  }, [getData, updateData]);

  /**
   * Handles errors during pagination
   */
  const handlePaginationError = useCallback((waveId_1: string, error: unknown) => {
    // Clear loading state
    if (paginationStates.current[waveId_1]) {
      paginationStates.current[waveId_1].isLoading = false;
      paginationStates.current[waveId_1].promise = null;
    }

    // Reset loading indicator in store
    const currentData_1 = getData(waveId_1);
    if (currentData_1) {
      updateData({
        key: waveId_1,
        isLoadingNextPage: false
      });
    }

    // Handle abort errors differently
    if (error instanceof DOMException && error.name === "AbortError") {
      return;
    }
    console.error(`[WaveDataManager] Error fetching more messages for ${waveId_1}:`, error);
  }, [getData, updateData]);

  /**
   * Fetches the next page of data for a wave
   */
  const fetchNextPage = useCallback(async (props: NextPageProps): Promise<(ApiDrop | ApiLightDrop)[] | null> => {
    // Get current state
    const currentData_2 = getData(props.waveId);
    if (!currentData_2) {
      return null;
    }

    // Don't proceed if already loading or if no next page
    if (currentData_2.isLoadingNextPage || !currentData_2.hasNextPage) {
      return null;
    }

    // Check if already has a pagination loading state
    const paginationState = paginationStates.current[props.waveId] ?? {
      isLoading: false,
      promise: null
    };
    paginationStates.current[props.waveId] = paginationState;

    // If already loading, return the existing promise
    if (paginationState.isLoading && paginationState.promise) {
      return paginationState.promise;
    }

    // Get the oldest message serial number for pagination
    const oldestSerialNo = getOldestMessageSerialNo(props.waveId);
    if (!oldestSerialNo) {
      return null;
    }

    // Mark as loading
    paginationState.isLoading = true;

    // Update store to show loading state
    updateData({
      key: props.waveId,
      isLoadingNextPage: true
    });

    // Setup abort controller
    const controller = createController(props.waveId);

    // Create promise for the request
    const rawPromise = props.type === DropSize.FULL ? fetchWaveMessages(props.waveId, oldestSerialNo, controller.signal) : fetchLightWaveMessages(props.waveId, oldestSerialNo, props.targetSerialNo, controller.signal);
    const handledPromise = rawPromise.then(drops => updateWithPaginatedData(props.waveId, drops)).catch(error_0 => {
      handlePaginationError(props.waveId, error_0);
      return null;
    }).finally(() => cleanupController(props.waveId, controller));

    // Store the promise so concurrent callers can await the same one
    paginationState.promise = handledPromise;
    return handledPromise;
  }, [getData, updateData, getOldestMessageSerialNo, createController, updateWithPaginatedData, handlePaginationError, cleanupController]);

  /**
   * Cancels a pagination fetch if one is in progress
   */
  const cancelPaginationFetch = useCallback((waveId_2: string) => {
    // Cancel the abort controller
    cancelAbort(waveId_2);

    // Clear pagination state
    if (paginationStates.current[waveId_2]) {
      paginationStates.current[waveId_2].isLoading = false;
      paginationStates.current[waveId_2].promise = null;
    }

    // Reset loading indicator in store
    const currentData_3 = getData(waveId_2);
    if (currentData_3?.isLoadingNextPage) {
      updateData({
        key: waveId_2,
        isLoadingNextPage: false
      });
    }
  }, [cancelAbort, getData, updateData]);

  /**
   * Internal function to process the queue for fetching around a serial number.
   */
  const _processAroundSerialNoQueue = useCallback(async (waveId_3: string): Promise<void> => {
    const state = aroundSerialNoStates.current[waveId_3];
    if (!state) {
      return; // No state for this waveId
    }

    // Guard 1: Already fetching
    if (state.isFetching) {
      return;
    }

    // Guard 2: No pending request
    const serialToFetch = determineSerialToFetch(state.pendingSerialNo);
    if (serialToFetch === null) {
      return;
    }

    // Mark as busy and clear pending request
    state.isFetching = true;
    state.pendingSerialNo = null;

    // Unique key for abort controller, distinct from pagination
    const abortKey = `${waveId_3}-around`;
    const controller_0 = createController(abortKey);
    try {
      const result = await fetchAroundSerialNoWaveMessages(waveId_3, serialToFetch, controller_0.signal);
      aroundQueueLastSuccessfullyFetchedSerialNoRef.current = serialToFetch;
      if (result) {
        updateData({
          key: waveId_3,
          drops: result.map(drop_1 => {
            return {
              ...drop_1,
              type: DropSize.FULL,
              stableKey: drop_1.id,
              stableHash: drop_1.id
            };
          })
        });
        if (result.length > 0) {
          const serials = result.map(drop_2 => drop_2.serial_no);
          aroundQueueLastFetchedMinSerialNoRef.current = Math.min(...serials);
          aroundQueueLastFetchedMaxSerialNoRef.current = Math.max(...serials);
        } else {
          aroundQueueLastFetchedMinSerialNoRef.current = null;
          aroundQueueLastFetchedMaxSerialNoRef.current = null;
          console.log(`[WavePagination] Fetched around serial no ${serialToFetch}, received an empty list of drops.`);
        }
      } else {
        aroundQueueLastFetchedMinSerialNoRef.current = null;
        aroundQueueLastFetchedMaxSerialNoRef.current = null;
        console.log(`[WavePagination] Fetched around serial no ${serialToFetch}, no new data (null result).`);
      }
    } catch (error_1) {
      if (!(error_1 instanceof DOMException && error_1.name === "AbortError")) {
        console.error(`[WavePagination] Error fetching around serial no ${serialToFetch} for wave ${waveId_3}:`, error_1);
      } else {
        console.log(`[WavePagination] Fetch around ${serialToFetch} aborted.`);
      }
    } finally {
      state.isFetching = false;
      cleanupController(abortKey, controller_0);
      // Trigger processing again in case a new request came in during the fetch
      void _processAroundSerialNoQueue(waveId_3);
    }
  }, [createController, cleanupController, updateData, determineSerialToFetch]);

  /**
   * Requests fetching messages around a specific serial number.
   * Ensures only the latest request is processed after the current fetch completes.
   */
  const fetchAroundSerialNo = useCallback((waveId_4: string, serialNo: number): void => {
    // Initialize state if it doesn't exist
    if (!aroundSerialNoStates.current[waveId_4]) {
      aroundSerialNoStates.current[waveId_4] = {
        isFetching: false,
        pendingSerialNo: null
      };
    }

    // Store the latest requested serial number
    aroundSerialNoStates.current[waveId_4].pendingSerialNo = serialNo;

    // If a request is already in flight, abort it so only the latest will be processed
    if (aroundSerialNoStates.current[waveId_4].isFetching) {
      cancelAbort(`${waveId_4}-around`);
    }

    // Trigger the processing queue
    void _processAroundSerialNoQueue(waveId_4);
  }, [cancelAbort, _processAroundSerialNoQueue] // Dependency: the internal processing function
  );
  return {
    fetchNextPage,
    cancelPaginationFetch,
    fetchAroundSerialNo // Expose the new function
  };
}